import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Bell, RefreshCw, MessageCircle, Volume2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEM_COLORS = { red: "bg-rose-500", amber: "bg-amber-400", green: "bg-emerald-400" };
const SEM_LABELS = { red: "caído", amber: "sin acuse", green: "confirmado" };

// Bloque 1 — vistas diferidas del Corte 6: A1 semáforo · A4 plataformas · A5 pagos con push caído · A6 audio
const PushHealthPanel = () => {
  const [data, setData] = useState(null);
  const [audioCfg, setAudioCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchAll = async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/push-health/summary`),
        axios.get(`${API}/admin/audio-config`),
      ]);
      setData(r1.data);
      setAudioCfg(r2.data);
    } catch (_) { /* panel es best-effort */ }
  };

  useEffect(() => { fetchAll(); }, []);

  const saveAudio = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/audio-config`, {
        in_app_sound_enabled: !!audioCfg.in_app_sound_enabled,
        volume_normal_pct: Number(audioCfg.volume_normal_pct) || 0,
        volume_emergency_pct: Number(audioCfg.volume_emergency_pct) || 0,
      });
      setAudioCfg(r.data);
      toast.success("Config de audio guardada");
    } catch (_) {
      toast.error("No se pudo guardar la config de audio");
    } finally {
      setSaving(false);
    }
  };

  const t = data?.totals;

  return (
    <Card className="bg-slate-800 border-slate-700 mt-6" data-testid="push-health-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white flex items-center gap-2">
          <Bell className="w-5 h-5 text-amber-400" />
          Salud de notificaciones push
        </CardTitle>
        <Button onClick={fetchAll} className="bg-amber-600 hover:bg-amber-500 text-white h-8 text-xs" data-testid="push-health-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </CardHeader>
      <CardContent>
        {!data ? (
          <p className="text-slate-500 text-sm">Cargando…</p>
        ) : (
          <div className="space-y-5">
            {/* A1 — semáforo por usuario (peor caso de sus dispositivos) */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <p className="text-xs text-slate-500">A1 · Semáforo por dispositivo (peor caso por usuario)</p>
                <span className="text-[10px] text-rose-300" data-testid="push-health-total-red">🔴 {t.red}</span>
                <span className="text-[10px] text-amber-300" data-testid="push-health-total-amber">🟠 {t.amber}</span>
                <span className="text-[10px] text-emerald-300" data-testid="push-health-total-green">🟢 {t.green}</span>
              </div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="push-health-semaforo-list">
                {data.semaforo_users.length === 0 && <p className="text-slate-500 text-xs">Sin dispositivos registrados.</p>}
                {data.semaforo_users.map((u) => (
                  <div key={u.user_id}>
                    <button
                      className="w-full flex items-center gap-2 text-xs text-left hover:bg-slate-700/40 rounded px-1 py-0.5"
                      onClick={() => setExpanded(expanded === u.user_id ? null : u.user_id)}
                      data-testid={`push-health-user-${u.user_id}`}
                    >
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${SEM_COLORS[u.semaforo]}`} />
                      <span className="text-slate-200 truncate">{u.email || u.full_name || u.user_id}</span>
                      <span className="text-[10px] text-slate-500">{u.devices.length} disp.</span>
                      {u.membership_status === "active" && <span className="text-[10px] text-emerald-400/80">pago activo</span>}
                      <span className="ml-auto text-[10px] text-slate-500">{SEM_LABELS[u.semaforo]}</span>
                    </button>
                    {expanded === u.user_id && (
                      <div className="ml-5 mt-1 space-y-0.5">
                        {u.devices.map((d, i) => (
                          <div key={i} className="flex items-center gap-2 text-[11px] text-slate-400">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${SEM_COLORS[d.status]}`} />
                            <span>{d.platform}</span>
                            {!d.confirmable && d.status === "amber" && <span className="text-slate-500">(no confirmable en esta plataforma)</span>}
                            {d.failure_reason && <span className="text-rose-400/80">{d.failure_reason}{d.http_status ? ` (${d.http_status})` : ""}</span>}
                            {d.last_ack_at && <span className="text-emerald-400/70">acuse {new Date(d.last_ack_at).toLocaleDateString("es-PE")}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">🟢 = ≥1 acuse del SW · 🟠 = válida sin acuse (iOS no confirmable) · 🔴 = suscripción muerta (404/410/401/403).</p>
            </div>

            {/* A4 — distribución por plataforma */}
            <div className="border-t border-slate-700 pt-3">
              <p className="text-xs text-slate-500 mb-2">A4 · Distribución por plataforma</p>
              <div className="flex flex-wrap gap-2" data-testid="push-health-platforms">
                {Object.entries(data.platform_distribution || {}).map(([plat, v]) => (
                  <span key={plat} className="text-[11px] px-2.5 py-1 rounded-full border border-slate-600 text-slate-300 bg-slate-900/60">
                    {plat}: <b className="text-white">{v.total}</b>
                    <span className="text-emerald-400/80"> · {v.enabled} activas</span>
                    <span className="text-sky-400/80"> · {v.acked} con acuse</span>
                  </span>
                ))}
                {Object.keys(data.platform_distribution || {}).length === 0 && <span className="text-slate-500 text-xs">—</span>}
              </div>
            </div>

            {/* A5 — pagos con push caído */}
            <div className="border-t border-slate-700 pt-3">
              <p className="text-xs text-slate-500 mb-2">A5 · Pagos activos con notificaciones caídas (todos los dispositivos 🔴)</p>
              <div className="space-y-1.5" data-testid="push-health-paid-dead">
                {(data.paid_dead_push || []).length === 0 ? (
                  <p className="text-emerald-400/80 text-xs" data-testid="push-health-paid-dead-empty">Ninguno — todos los pagos activos tienen al menos un dispositivo vivo.</p>
                ) : (
                  data.paid_dead_push.map((u) => (
                    <div key={u.user_id} className="flex items-center gap-2 text-xs bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">
                      <span className="text-rose-200 truncate">{u.email || u.full_name}</span>
                      <span className="text-[10px] text-slate-400">vence {u.membership_expires_at ? new Date(u.membership_expires_at).toLocaleDateString("es-PE") : "—"}</span>
                      {u.whatsapp_link && (
                        <a href={u.whatsapp_link} target="_blank" rel="noreferrer" className="ml-auto" data-testid={`push-health-wa-${u.user_id}`}>
                          <Button className="bg-emerald-600 hover:bg-emerald-500 text-white h-6 text-[10px] px-2">
                            <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                          </Button>
                        </a>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* A6 — config global del audio in-app */}
            {audioCfg && (
              <div className="border-t border-slate-700 pt-3">
                <p className="text-xs text-slate-500 mb-2 flex items-center gap-1.5"><Volume2 className="w-3.5 h-3.5" /> A6 · Audio in-app (global)</p>
                <div className="flex flex-wrap items-end gap-4">
                  <label className="flex items-center gap-2 text-xs text-slate-300">
                    <input
                      type="checkbox"
                      checked={!!audioCfg.in_app_sound_enabled}
                      onChange={(e) => setAudioCfg({ ...audioCfg, in_app_sound_enabled: e.target.checked })}
                      data-testid="audio-config-enabled"
                    />
                    Sonido in-app activo
                  </label>
                  <label className="text-xs text-slate-400">
                    Volumen normal (%)
                    <input
                      type="number" min="0" max="100"
                      value={audioCfg.volume_normal_pct}
                      onChange={(e) => setAudioCfg({ ...audioCfg, volume_normal_pct: e.target.value })}
                      className="block w-20 mt-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white"
                      data-testid="audio-config-volume-normal"
                    />
                  </label>
                  <label className="text-xs text-slate-400">
                    Volumen emergencia (%)
                    <input
                      type="number" min="0" max="100"
                      value={audioCfg.volume_emergency_pct}
                      onChange={(e) => setAudioCfg({ ...audioCfg, volume_emergency_pct: e.target.value })}
                      className="block w-20 mt-1 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-white"
                      data-testid="audio-config-volume-emergency"
                    />
                  </label>
                  <Button onClick={saveAudio} disabled={saving} className="bg-sky-600 hover:bg-sky-500 text-white h-8 text-xs" data-testid="audio-config-save-btn">
                    {saving ? "Guardando…" : "Guardar"}
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500 mt-2">Afecta solo el sonido sintetizado dentro de la app abierta. Las notificaciones push del sistema no cambian.</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PushHealthPanel;
